import { isDefaultVintageYear } from '@helpers/vintage';
import { isPartnerWinery } from '@helpers/winery';
import { FilterButton, Menu, MenuItem, Sentinel, useBreakpoint } from '@vivino/js-react-common-ui';
import { CartsContext, getCartByMerchantId, useI18N } from '@vivino/js-web-common';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { hasVintageSubstitution } from 'vivino-js/helpers/availability';
import {
  getDiscountedFromPrice,
  getPriceAmount,
  isOfferPrice,
  isShowPricePerLiter,
  pricePerLiterString,
} from 'vivino-js/helpers/price';
import Price from 'vivino-ui/atoms/Price';

import { isLinkoutMerchant } from '@lib/merchants';

import FreeBottleDiscountBanner from '@components/Checkout/plus/FreeBottleDiscountBanner';
import { ErrorBoundary } from '@components/ErrorBoundary/ErrorBoundary';
import ExternalBuySection from '@components/ExternalBuySection/ExternalBuySection';
import VFMScale from '@components/VFMScale/VFMScale';
import ExclamationMark from '@components/icons/ExclamationMark/ExclamationMark';

import ShippingPolicy from './components/ShippingPolicy';
import BuyingOptions from './shared/components/BuyingOptions';
import AddToCartButton from 'vivino-ui/components/AddToCartButton';
import MerchantDetails from 'vivino-ui/components/MerchantDetails';
import QuantityPicker from 'vivino-ui/components/QuantityPicker';

import styles from './purchaseAvailability.scss';

const TRANSLATIONS_PATH = 'components.deal_wine_page.purchase_availability';
const TRANSLATIONS = {
  priceIsPerBottle: `${TRANSLATIONS_PATH}.price_is_per_bottle`,
  vintageYear: `${TRANSLATIONS_PATH}.vintage_year`,
  vintage: `${TRANSLATIONS_PATH}.vintage`,
  vintageYearNotAvailableNow: `${TRANSLATIONS_PATH}.vintage_year_not_available_now`,
  butWeHaveThisVintageInstead: `${TRANSLATIONS_PATH}.but_we_have_this_vintage_instead`,
  unknownVintageForThePrice: `${TRANSLATIONS_PATH}.unknown_vintage_for_the_price`,
  item: 'helpers.vintage_helper.item',
  bottles: 'components.shared.quantity_picker.bottles',
};

const PurchaseAvailability = ({
  availability,
  calculatedPrice,
  countryCode,
  loadingQuantity,
  merchant,
  merchantsWithPrices = [],
  onQuantityChange,
  onQuantityChanging,
  quantityHasChanged,
  onSelectChange,
  price,
  stateCode,
  vintage,
  errorMessage,
  valueForMoney,
  dropdownCheckoutPrices,
  isVintageSubstitutionDisabled,
}) => {
  const { t } = useI18N();
  const { isTablet, isMobile } = useBreakpoint();

  const isLinkout = isLinkoutMerchant(merchant);
  const winery = vintage?.wine?.winery;
  const merchantInformation = isLinkout
    ? {
        merchantName: merchant?.name,
        merchantId: merchant?.id,
        icon: merchant?.image?.variations?.small_square || merchant?.image?.location,
        disclaimer: merchant?.disclaimer,
        deliveryInformation: merchant?.selling_points?.[0]?.description,
        link: price?.url,
        vintageId: vintage?.id,
        wineryId: winery?.id,
        priceAmount: price?.amount,
        currency: price?.currency?.code,
        sponsored: isPartnerWinery(winery),
      }
    : null;

  const [anchorEl, setAnchorEl] = useState(null);
  const [buttonSticky, setButtonSticky] = useState(false);

  const handleDisplayVintageOptions = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseVintageOptions = () => {
    setAnchorEl(null);
  };

  const handleSelectVintage = (params) => {
    onSelectChange(params);
    handleCloseVintageOptions();
  };
  const showBuyingOptions = (price) => {
    return merchantsWithPrices.length > 1 && !isOfferPrice(price);
  };

  const { carts } = useContext(CartsContext);

  let cartFromMerchant;

  if (carts.length > 0) {
    cartFromMerchant = getCartByMerchantId(carts, merchant?.id);
  }

  const bottleType = price?.bottle_type;

  const merchantSection = (
    <div className={styles.merchantSection}>
      {calculatedPrice?.promo_message && (
        <div className={cx(styles.row, styles.promoMessage)}>{calculatedPrice?.promo_message}</div>
      )}
      {!isLinkout && (
        <ShippingPolicy
          merchantId={merchant?.id}
          quantity={calculatedPrice?.bottle_count}
          quantityHasChanged={quantityHasChanged}
          totalAmount={calculatedPrice?.subtotal_amount}
          shippingEstimate={merchant?.shipping_estimate}
          countryCode={countryCode}
        />
      )}
      <div className={cx(styles.merchantDetails)}>
        <MerchantDetails
          merchant={merchant}
          stateCode={stateCode}
          countryCode={countryCode}
          price={price}
        />
      </div>
      {showBuyingOptions(price) && (
        <div className={styles.buyingOptions}>
          <BuyingOptions vintage={vintage} />
        </div>
      )}

      {cartFromMerchant && !['AK', 'HI'].includes(String(stateCode).toUpperCase()) && (
        <FreeBottleDiscountBanner
          bottleCount={calculatedPrice?.bottle_count}
          cart={cartFromMerchant}
          className={styles.freeBottleDiscount}
        />
      )}
    </div>
  );

  const priceAmount = getPriceAmount(calculatedPrice, price);
  const discountedFromPrice = getDiscountedFromPrice(calculatedPrice, price);
  const hasDiscountedPrice = discountedFromPrice > 0 && priceAmount < discountedFromPrice;
  const availableVintageYear = availability?.vintage?.year;

  const selectedVintage = dropdownCheckoutPrices?.find(
    (dropdownItem) => dropdownItem.id === availability?.vintage?.id
  );

  return (
    <div className={styles.purchaseAvailabilityContent}>
      <div className={styles.priceInformation}>
        <div className={cx(styles.row, styles.prices)}>
          <Price className={styles.currentPrice} amount={priceAmount} currency={price.currency} />
          {hasDiscountedPrice && (
            <Price
              strike
              className={styles.retailPrice}
              amount={discountedFromPrice}
              currency={price.currency}
              dataTestId="discountPrice"
            />
          )}
        </div>
        <div className={cx(styles.row, styles.volume)}>
          {t(TRANSLATIONS.priceIsPerBottle, {
            bottle_type: bottleType?.short_name || t(TRANSLATIONS.item),
          })}
          {isShowPricePerLiter(merchant?.country?.code) &&
            `, ${pricePerLiterString({
              currency: price.currency,
              pricePerLiter: calculatedPrice?.price_per_liter,
            })}`}
        </div>
        {valueForMoney && (
          <ErrorBoundary>
            <div className={styles.vfmScale} data-testid="vfm-scale">
              <VFMScale
                countryCode={countryCode}
                qprScores={valueForMoney}
                vintage={selectedVintage || vintage}
                price={{
                  ...price,
                  id: calculatedPrice?.best_price_id || price.id,
                  amount: calculatedPrice?.price_per_bottle || price.amount,
                }}
              />
            </div>
          </ErrorBoundary>
        )}
        {isTablet && merchantSection}
      </div>
      <div className={cx(styles.row, styles.cta)}>
        {!isVintageSubstitutionDisabled && hasVintageSubstitution(vintage, availability) && (
          <div className={styles.vintageSubstitution}>
            <div>
              <ExclamationMark className={styles.exclamationMark} />
            </div>
            <div>
              {isDefaultVintageYear(availableVintageYear)
                ? t(TRANSLATIONS.unknownVintageForThePrice)
                : t(TRANSLATIONS.butWeHaveThisVintageInstead, {
                    vintage_year_not_available_now: (
                      <span className={styles.emphasized} key="notAvailableVintage">
                        {t(TRANSLATIONS.vintageYearNotAvailableNow, {
                          year: vintage?.year,
                        })}
                      </span>
                    ),
                    year: (
                      <span className={styles.emphasized} key="availableVintage">
                        {availableVintageYear}
                      </span>
                    ),
                  })}
            </div>
          </div>
        )}
        {errorMessage && (
          <div className={styles.errorMessage} data-testid="error-message">
            {errorMessage}
          </div>
        )}
        <div className={styles.selectors}>
          {!isLinkout && (
            <div className={styles.actionButtons}>
              <span className={styles.text}>{t(TRANSLATIONS.bottles)}</span>
              <QuantityPicker
                increment={calculatedPrice?.increment}
                minimumValue={calculatedPrice?.minimum_bottle_count}
                maximumValue={price.inventory_bottles_count}
                onQuantityChange={onQuantityChange}
                onQuantityChanging={onQuantityChanging}
                initialValue={calculatedPrice?.bottle_count}
                className={styles.quantityPicker}
              />
            </div>
          )}
          {dropdownCheckoutPrices?.length > 0 && (
            <>
              <div className={styles.actionButtons}>
                <span className={styles.text}>{t(TRANSLATIONS.vintage)}</span>
                <FilterButton
                  fullWidth
                  onClick={handleDisplayVintageOptions}
                  label={selectedVintage?.name || t(TRANSLATIONS.vintage)}
                />
              </div>
              <Menu
                open={Boolean(anchorEl)}
                onClose={handleCloseVintageOptions}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {dropdownCheckoutPrices?.length > 0 &&
                  dropdownCheckoutPrices.map((dropdownItem) => (
                    <MenuItem
                      style={{ minWidth: '180px' }}
                      key={dropdownItem.id}
                      value={dropdownItem.id}
                      onClick={handleSelectVintage}
                      selected={availability?.vintage?.id === dropdownItem.id}
                    >
                      {dropdownItem.name}
                    </MenuItem>
                  ))}
              </Menu>
            </>
          )}
        </div>
        <Sentinel
          onIntersect={() => setButtonSticky(false)}
          onIntersectLeave={() => setButtonSticky(true)}
        />
        <div className={cx({ [styles.stickyAddToCartButton]: isMobile && buttonSticky })}>
          {isLinkout ? (
            <ExternalBuySection {...merchantInformation} />
          ) : (
            <AddToCartButton
              hidePrice
              fullWidth
              price={price}
              disabled={loadingQuantity}
              bottleCount={calculatedPrice?.bottle_count}
              seenVintageId={Number(availability?.vintage?.id || vintage.id)}
            />
          )}
        </div>
      </div>

      {!isTablet && merchantSection}
    </div>
  );
};

PurchaseAvailability.propTypes = {
  availability: PropTypes.object,
  calculatedPrice: PropTypes.object,
  currency: PropTypes.object,
  countryCode: PropTypes.string,
  errorMessage: PropTypes.string,
  loadingQuantity: PropTypes.bool,
  merchant: PropTypes.object,
  merchantsWithPrices: PropTypes.array,
  merchantShippingMessage: PropTypes.string,
  onQuantityChange: PropTypes.func,
  onQuantityChanging: PropTypes.func,
  quantityHasChanged: PropTypes.bool,
  onSelectChange: PropTypes.func,
  price: PropTypes.object,
  stateCode: PropTypes.string,
  vintage: PropTypes.object,
  dropdownCheckoutPrices: PropTypes.array,
  isVintageSubstitutionDisabled: PropTypes.bool,
  valueForMoney: PropTypes.object,
};

export default PurchaseAvailability;
